import React from 'react';
import { Container, Typography, Box, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const UploadingVideos: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  return (
    <Box sx={{ minHeight: '100vh' }}>
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/help')}
          sx={{ mb: 4, color: 'text.secondary' }}
        >
          {t('help.backToHelp', 'Back to Help Center')}
        </Button>

        <Typography variant="overline" color="primary" sx={{ fontWeight: 600, letterSpacing: 2, display: 'block', mb: 2 }}>
          FOR CREATORS
        </Typography>

        <Typography variant="h3" component="h1" sx={{ mb: 3, fontWeight: 700 }}>
          Uploading Videos
        </Typography>

        <Typography variant="body1" color="text.secondary" sx={{ mb: 4, lineHeight: 1.85, fontSize: '1.1rem' }}>
          Uploading videos to IndeeClips is straightforward and designed to help you share your content with millions of viewers. Whether you're creating educational content, entertainment, or vlogs, our platform supports high-quality video uploads with flexible quality options.
        </Typography>

        <Typography variant="h5" sx={{ mt: 5, mb: 2, fontWeight: 700 }}>
          How to Upload a Video
        </Typography>
        <Box component="ol" sx={{ pl: 3, mb: 4 }}>
          <Typography component="li" variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
            Sign in to your IndeeClips account and make sure you have a channel created
          </Typography>
          <Typography component="li" variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
            Click the "Upload" button in the navigation menu or go to your channel page
          </Typography>
          <Typography component="li" variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
            Click "Select Video File" or drag and drop your video file into the upload area
          </Typography>
          <Typography component="li" variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
            Enter a compelling title for your video (maximum 100 characters recommended)
          </Typography>
          <Typography component="li" variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
            Write a detailed description explaining your video content, including relevant keywords
          </Typography>
          <Typography component="li" variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
            Upload a custom thumbnail image (recommended: 1280x720 pixels, JPG or PNG format)
          </Typography>
          <Typography component="li" variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
            Add relevant tags to help viewers discover your content
          </Typography>
          <Typography component="li" variant="body1" color="text.secondary" sx={{ lineHeight: 1.85 }}>
            Click "Upload" and wait for processing to complete before your video goes live
          </Typography>
        </Box>

        <Typography variant="h5" sx={{ mt: 5, mb: 2, fontWeight: 700 }}>
          Supported Video Formats and Specifications
        </Typography>
        <Box component="ul" sx={{ pl: 3, mb: 4 }}>
          <Typography component="li" variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
            <strong>File formats:</strong> MP4, MOV, AVI, WMV, FLV, MKV (MP4 recommended for best compatibility)
          </Typography>
          <Typography component="li" variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
            <strong>Maximum file size:</strong> 10 GB per video upload
          </Typography>
          <Typography component="li" variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
            <strong>Video resolution:</strong> Up to 4K (3840x2160) supported, with automatic quality options (360p, 480p, 720p, 1080p, 1440p, 2160p)
          </Typography>
          <Typography component="li" variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
            <strong>Aspect ratio:</strong> 16:9 recommended for standard videos, but other ratios are supported
          </Typography>
          <Typography component="li" variant="body1" color="text.secondary" sx={{ lineHeight: 1.85 }}>
            <strong>Frame rate:</strong> 24, 30, or 60 FPS (frames per second) recommended
          </Typography>
        </Box>

        <Typography variant="h5" sx={{ mt: 5, mb: 2, fontWeight: 700 }}>
          Upload Best Practices
        </Typography>
        <Box component="ul" sx={{ pl: 3, mb: 4 }}>
          <Typography component="li" variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
            <strong>Create engaging titles:</strong> Use clear, descriptive titles that accurately represent your content and include relevant keywords
          </Typography>
          <Typography component="li" variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
            <strong>Write detailed descriptions:</strong> Include timestamps, links, and comprehensive information about your video content
          </Typography>
          <Typography component="li" variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
            <strong>Design custom thumbnails:</strong> Eye-catching thumbnails significantly increase click-through rates. Use bold text and high-contrast images
          </Typography>
          <Typography component="li" variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
            <strong>Upload during peak hours:</strong> For Indian audiences, consider uploading between 6-9 PM IST when viewership is highest
          </Typography>
          <Typography component="li" variant="body1" color="text.secondary" sx={{ lineHeight: 1.85 }}>
            <strong>Check processing status:</strong> After uploading, allow time for video processing before sharing your link
          </Typography>
        </Box>

        <Typography variant="h5" sx={{ mt: 5, mb: 2, fontWeight: 700 }}>
          After Your Video is Uploaded
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3, lineHeight: 1.85 }}>
          Once processing is complete, your video will be available on your channel and in search results. You can view analytics, edit video details, or delete the video from your channel management page. Videos are automatically available in multiple quality options based on the original upload quality.
        </Typography>

        <Box
          sx={{
            mt: 6,
            p: 4,
            bgcolor: 'background.paper',
            borderRadius: 2,
            border: 1,
            borderColor: 'divider',
          }}
        >
          <Typography variant="h5" sx={{ mb: 2, fontWeight: 700 }}>
            Having Trouble Uploading?
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.85 }}>
            If your upload fails or you experience processing issues, check your internet connection and file format. For persistent problems, contact our support team. Email{' '}
            <Typography
              component="a"
              href="mailto:contact@indeeclips.com"
              sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
            >
              contact@indeeclips.com
            </Typography>
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default UploadingVideos;
